import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Upload,
  message,
  Steps,
  Card,
  Table,
  Tag,
  Space,
  Divider,
  Empty,
  Spin,
  Alert,
  Row,
  Col,
  Statistic,
  Progress,
} from "antd";
import {
  DownloadOutlined,
  UploadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import bulkLeadService from "../../services/bulkLeadService";

const BulkUploadModal = ({ visible, onClose, onSuccess }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [fileList, setFileList] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState(null);
  const [templateSchema, setTemplateSchema] = useState(null);
  const [loadingSchema, setLoadingSchema] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadJobId, setUploadJobId] = useState(null);
  const [statusPollingId, setStatusPollingId] = useState(null);
  const [uploadStatus, setUploadStatus] = useState(null); // 'success' | 'failed' | 'cancelled'

  useEffect(() => {
    if (visible && currentStep === 0) {
      loadSchema();
    }
  }, [visible, currentStep]);

  const loadSchema = async () => {
    setLoadingSchema(true);
    const result = await bulkLeadService.getTemplateSchema();
    if (result.success) {
      setTemplateSchema(result.data);
    }
    setLoadingSchema(false);
  };

  const handleDownloadTemplate = async () => {
    message.loading({ content: "Downloading template...", key: "download" });
    const result = await bulkLeadService.downloadTemplate();
    if (result.success) {
      message.success({
        content: "Template downloaded successfully",
        key: "download",
      });
    } else {
      message.error({
        content: result.error || "Failed to download template",
        key: "download",
      });
    }
  };

  const handleUpload = async () => {
    if (fileList.length === 0) {
      message.error("Please select a file to upload");
      return;
    }

    const fileItem = fileList[0];
    const rawFile = fileItem.originFileObj || fileItem;

    // Validate file type
    const validExtensions = [".csv", ".xlsx", ".xls"];
    const fileName = (rawFile.name || fileItem.name || "").toLowerCase();
    const hasValidExtension = validExtensions.some((ext) =>
      fileName.endsWith(ext)
    );

    if (!hasValidExtension) {
      message.error("Please upload a CSV or Excel file");
      return;
    }

    // Generate a simple client-side jobId for tracking
    const jobId = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    setUploadJobId(jobId);

    // Start polling server-side processing progress
    const intervalId = window.setInterval(async () => {
      const status = await bulkLeadService.getBulkUploadStatus(jobId);
      if (status.success && status.data) {
        setUploadProgress(status.data.percent || 0);

        if (
          status.data.status === "completed" ||
          status.data.status === "failed" ||
          status.data.status === "cancelled"
        ) {
          window.clearInterval(intervalId);
          setStatusPollingId(null);
        }
      }
    }, 1000);
    setStatusPollingId(intervalId);

    setUploading(true);
    const result = await bulkLeadService.bulkUploadLeads(rawFile, jobId);
    setUploading(false);

    if (result.success) {
      setUploadResults(result.data);
      setCurrentStep(2);
      setUploadStatus("success");
      message.success(result.message);
      if (onSuccess) {
        onSuccess();
      }
    } else {
      // Distinguish between user-cancelled uploads and actual failures
      if (result.message && result.message.toLowerCase().includes("cancelled")) {
        setUploadStatus("cancelled");
        message.warning(result.message || "Uploading cancelled by the user");
      } else {
        setUploadStatus("failed");
        message.error(result.error || result.message || "Failed to upload leads");
      }
      if (result.data) {
        setUploadResults(result.data);
        setCurrentStep(2);
      }
    }
  };

  const handleFileChange = ({ fileList: newFileList }) => {
    setFileList(newFileList);
  };

  const handleReset = () => {
    setCurrentStep(0);
    setFileList([]);
    setUploadResults(null);
    setUploadProgress(0);
    setUploadJobId(null);
    setUploadStatus(null);

    if (statusPollingId) {
      window.clearInterval(statusPollingId);
      setStatusPollingId(null);
    }
  };

  const handleCancelUpload = async () => {
    try {
      if (uploadJobId) {
        await bulkLeadService.cancelBulkUpload(uploadJobId);
      }
    } catch (e) {
      // Swallow errors here; UI will still reset
    } finally {
      // Do not close the modal here; bulkUploadLeads will finish and
      // handleUpload will move the stepper to the Results view with
      // a cancellation message.
    }
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const successColumns = [
    {
      title: "Row",
      dataIndex: "row",
      key: "row",
      width: 60,
    },
    {
      title: "Name",
      key: "name",
      render: (_, record) => `${record.firstName} ${record.lastName}`,
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Lead ID",
      dataIndex: "leadId",
      key: "leadId",
      render: (id) => <span className="text-xs text-gray-500">{id}</span>,
    },
  ];

  const failureColumns = [
    {
      title: "Row",
      dataIndex: "row",
      key: "row",
      width: 60,
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Error",
      dataIndex: "error",
      key: "error",
      render: (error) => (
        <span className="text-red-600 text-sm">{error}</span>
      ),
    },
  ];

  const schemaColumns = [
    {
      title: "Field Name",
      dataIndex: "name",
      key: "name",
      render: (text) => <strong>{text}</strong>,
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      render: (type) => <Tag color="blue">{type}</Tag>,
    },
    {
      title: "Required",
      dataIndex: "required",
      key: "required",
      render: (required) => (
        <Tag color={required ? "red" : "green"}>
          {required ? "Yes" : "Optional"}
        </Tag>
      ),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
    },
  ];

  const successfulResults = uploadResults?.successful || [];
  const failedResults = uploadResults?.failed || [];
  const totalRecords =
    uploadResults?.totalRecords ?? successfulResults.length + failedResults.length;

  return (
    <Modal
      title="Bulk Upload Leads"
      open={visible}
      onCancel={handleClose}
      maskClosable={!uploading ? true : false}
      closable={!uploading}
      keyboard={!uploading}
      width={900}
      footer={null}
      styles={{ body: { maxHeight: "70vh", overflowY: "auto" } }}
    >
      <Steps
        current={currentStep}
        items={[
          { title: "Download Template", description: "Get the template file" },
          { title: "Upload File", description: "Upload your leads" },
          { title: "Results", description: "Review upload results" },
        ]}
        className="mb-6"
      />

      {/* Step 0: Download Template */}
      {currentStep === 0 && (
        <div className="space-y-6">
          <Alert
            message="Download the template to see the required format"
            type="info"
            showIcon
            className="mb-4"
          />

          <Spin spinning={loadingSchema}>
            {templateSchema && (
              <Card title="Template Schema" className="mb-6">
                <Table
                  columns={schemaColumns}
                  dataSource={templateSchema.fields}
                  rowKey="name"
                  pagination={false}
                  size="small"
                />
              </Card>
            )}
          </Spin>

          <div className="flex gap-3">
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={handleDownloadTemplate}
              size="large"
            >
              Download Template
            </Button>
            <Button
              onClick={() => setCurrentStep(1)}
              size="large"
            >
              Next: Upload File
            </Button>
          </div>
        </div>
      )}

      {/* Step 1: Upload File */}
      {currentStep === 1 && (
        <div className="space-y-6">
          <Alert
            message="Upload a CSV or Excel file with your leads"
            description="The file should follow the template format. Leads will be assigned to the tenant admin. Maximum 2000 data rows (excluding header) are allowed per upload."
            type="info"
            showIcon
            className="mb-4"
          />

          <Card title="Select File">
            <Upload
              fileList={fileList}
              onChange={handleFileChange}
              beforeUpload={() => false}
              accept=".csv,.xlsx,.xls"
              maxCount={1}
              listType="text"
            >
              <Button icon={<UploadOutlined />}>
                Select CSV or Excel File
              </Button>
            </Upload>
          </Card>

          {uploading && (
            <Card>
              <div className="space-y-2">
                <div>Uploading file...</div>
                <Progress
                  percent={uploadProgress}
                  status={uploadProgress === 100 ? "success" : "active"}
                />
              </div>
            </Card>
          )}

          <div className="flex gap-3">
            <Button onClick={handleCancelUpload}>Cancel</Button>
            <Button
              type="primary"
              icon={<UploadOutlined />}
              onClick={handleUpload}
              loading={uploading}
              disabled={fileList.length === 0}
              size="large"
            >
              Upload Leads
            </Button>
          </div>
        </div>
      )}

      {/* Step 2: Results */}
      {currentStep === 2 && uploadResults && (
        <div className="space-y-6">
          {uploadStatus === "cancelled" && (
            <Alert
              type="warning"
              message="Bulk upload cancelled by user"
              description="Any leads created during this upload have been reverted."
              showIcon
            />
          )}

          <Row gutter={16}>
            <Col span={8}>
              <Statistic
                title="Total Records"
                value={totalRecords}
                prefix={<CheckCircleOutlined />}
              />
            </Col>
            <Col span={8}>
              <Statistic
                title="Successful"
                value={successfulResults.length}
                valueStyle={{ color: "#52c41a" }}
                prefix={<CheckCircleOutlined />}
              />
            </Col>
            <Col span={8}>
              <Statistic
                title="Failed"
                value={failedResults.length}
                valueStyle={{ color: failedResults.length > 0 ? "#ff4d4f" : "#52c41a" }}
                prefix={<CloseCircleOutlined />}
              />
            </Col>
          </Row>

          {successfulResults.length > 0 && (
            <Card
              title={`Successfully Created (${successfulResults.length})`}
              type="inner"
            >
              <Table
                columns={successColumns}
                dataSource={successfulResults}
                rowKey="leadId"
                pagination={{ pageSize: 5 }}
                size="small"
              />
            </Card>
          )}

          {failedResults.length > 0 && (
            <Card
              title={`Failed (${failedResults.length})`}
              type="inner"
            >
              <Table
                columns={failureColumns}
                dataSource={failedResults}
                rowKey={(record) => `${record.row}-${record.email}`}
                pagination={{ pageSize: 5 }}
                size="small"
              />
            </Card>
          )}

          {successfulResults.length === 0 &&
            failedResults.length === 0 && (
              <Empty description="No results" />
            )}

          <div className="flex gap-3">
            <Button onClick={handleReset}>Upload Another File</Button>
            <Button type="primary" onClick={handleClose}>
              Close
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default BulkUploadModal;
